// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/ManageStaff/AddStaffModal.js
import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { AlertCircle, CheckCircle2, Clipboard, Loader2, X } from 'lucide-react';

const AddStaffModal = ({
  open,
  onClose,
  onSubmit,
  newStaff,
  setNewStaff,
  roles,
  isProcessing,
  submitStatus,
  setSubmitStatus,
  tenantId,
  userId,
  api,
  setStaff,
  setShowAddModal,
  onSuccess
}) => {
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneValue, setPhoneValue] = useState('');

  const getRoleDisplayLabel = (role) => {
    if (!role) return '';
    const normalized = String(role).trim().toLowerCase().replace(/[\s-]+/g, '_');
    if (normalized === 'coe') return 'Controller of Examination';
    if (normalized === 'coe_coordinator') return 'COE Coordinator';

    return String(role)
      .replace(/_/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const normalizeRoleForApi = (role) => {
    if (!role) return '';
    const normalized = String(role).trim().toLowerCase().replace(/[\s-]+/g, '_');
    if (normalized === 'controller_of_examination') return 'coe';
    return normalized;
  };

  const roleOptionsMap = new Map();
  (Array.isArray(roles) ? roles : []).forEach((role) => {
    const key = normalizeRoleForApi(role);
    if (key) {
      roleOptionsMap.set(key, getRoleDisplayLabel(role));
    }
  });
  if (!roleOptionsMap.has('coe_coordinator')) {
    roleOptionsMap.set('coe_coordinator', 'COE Coordinator');
  }
  const roleOptions = Array.from(roleOptionsMap.entries()).map(([value, label]) => ({ value, label }));

  const validatePhone = (value) => {
    if (!value) return 'Phone number is required';
    try {
      const phoneNumber = parsePhoneNumberFromString(value);
      if (!phoneNumber || !phoneNumber.isValid()) return 'Invalid phone number';
    } catch {
      return 'Invalid phone number';
    }
    return '';
  };

  useEffect(() => {
    if (open) {
      setFormErrors({});
      setTouched({});
      setSubmitStatus({ success: null, message: '', showPassword: false });
      setSuggestions([]);
      setIsSubmitting(false);
      setPhoneValue('');
    }
  }, [open, setSubmitStatus]);

  if (!open) return null;

  const validateField = (name, value) => {
    switch (name) {
      case 'firstName':
        return !value?.trim() ? 'First name is required' : '';
      case 'email':
        if (!value) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Invalid email format';
        
        const domain = value.split('@')[1];
        const commonDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com'];
        if (domain && !commonDomains.includes(domain) && value.includes('@')) {
          setSuggestions([`Did you mean @gmail.com?`]);
        } else {
          setSuggestions([]);
        }
        return '';
      case 'phoneNumber':
        return validatePhone(value);
      case 'role':
        return !value ? 'Please select a role' : '';
      default:
        return '';
    }
  };

  const handleFieldChange = (field, value) => {
    if (field === 'phoneNumber') {
      setPhoneValue(value);
      setNewStaff({ ...newStaff, [field]: value });
    } else {
      setNewStaff({ ...newStaff, [field]: value });
    }
    
    setTouched({ ...touched, [field]: true });
    const error = validateField(field, value);
    setFormErrors(prev => {
      const updated = { ...prev };
      if (error) {
        updated[field] = error;
      } else {
        delete updated[field];
      }
      return updated;
    });
    
    if (submitStatus.message) {
      setSubmitStatus({ success: null, message: '', showPassword: false });
    }
  };

  const handlePhoneChange = (value) => {
    let formattedValue = value.startsWith('+') ? value : `+${value}`;
    setPhoneValue(formattedValue);
    setNewStaff({ ...newStaff, phoneNumber: formattedValue });
    setTouched({ ...touched, phoneNumber: true });
    const error = validateField('phoneNumber', formattedValue);
    setFormErrors(prev => {
      const updated = { ...prev };
      if (error) {
        updated.phoneNumber = error;
      } else {
        delete updated.phoneNumber;
      }
      return updated;
    });
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    const error = validateField(field, newStaff[field]);
    setFormErrors(prev => {
      const updated = { ...prev };
      if (error) {
        updated[field] = error;
      } else {
        delete updated[field];
      }
      return updated;
    });
  };

  const validateForm = () => {
    const errors = {};
    const fields = ['firstName', 'email', 'phoneNumber', 'role'];
    fields.forEach(field => {
      const error = validateField(field, newStaff[field]);
      if (error) errors[field] = error;
    });
    setFormErrors(errors);
    setTouched(fields.reduce((acc, field) => ({ ...acc, [field]: true }), {}));
    return Object.keys(errors).length === 0;
  };

  const getFieldClassName = (field) => {
    const baseClass = "w-full px-3.5 py-2.5 text-sm bg-slate-50 border rounded-xl focus:bg-white focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none transition-all h-10";
    if (touched[field] && formErrors[field]) {
      return `${baseClass} border-red-500 text-red-900 focus:ring-red-500 focus:border-red-500`;
    }
    return `${baseClass} border-slate-200 text-slate-900 placeholder-slate-400`;
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setSubmitStatus({ success: false, message: 'Please fix the errors in the form.', showPassword: false });
      return;
    }
    if (isSubmitting) return;
    setIsSubmitting(true);
    setSubmitStatus({ success: null, message: 'Creating staff member...', processing: true, showPassword: false });

    try {
      if (!tenantId) throw new Error('Tenant ID is missing');

      const requestData = {
        firstName: newStaff.firstName?.trim(),
        lastName: newStaff.lastName?.trim() || '',
        email: newStaff.email?.toLowerCase().trim(),
        phoneNumber: newStaff.phoneNumber.startsWith('+') ? newStaff.phoneNumber : `+${newStaff.phoneNumber}`,
        role: normalizeRoleForApi(newStaff.role),
        tenantId,
        createdBy: userId
      };

      const tempId = `temp-${Date.now()}`;
      const optimisticStaff = {
        _id: tempId, id: tempId, ...requestData, createdAt: new Date().toISOString(), status: 'creating', isOptimistic: true
      };

      setStaff(prev => {
        if (prev.some(s => s.email === requestData.email)) {
          throw new Error('A staff member with this email already exists');
        }
        return [...prev, optimisticStaff];
      });

      const response = await api.post('/api/admin/add-user', requestData);

      setStaff(prev => 
        prev.map(staff => (staff.id === tempId || staff._id === tempId) ? {
          ...response.data,
          _id: response.data.userId || response.data._id,
          id: response.data.userId || response.data._id,
          firstName: requestData.firstName,
          lastName: requestData.lastName,
          email: requestData.email,
          phoneNumber: requestData.phoneNumber,
          role: requestData.role,
          status: 'active',
          createdAt: new Date().toISOString()
        } : staff)
      );

      setSubmitStatus({
        success: true,
        message: `Staff member ${newStaff.firstName} created successfully!`,
        showPassword: true,
        defaultPassword: response.data.defaultPassword || 'Truyon@1234',
        userId: response.data.userId || response.data._id
      });

      setNewStaff({ firstName: '', lastName: '', email: '', phoneNumber: '', role: 'faculty' });
      setPhoneValue('');
      if (onSuccess) onSuccess(response.data);

      setTimeout(() => {
        setShowAddModal(false);
        setSubmitStatus({ success: null, message: '', showPassword: false });
      }, 3000);

    } catch (err) {
      console.error(err);
      setStaff(prev => prev.filter(staff => !staff.isOptimistic));
      setSubmitStatus({
        success: false,
        message: err.response?.data?.message || err.response?.data?.error || err.message || 'Error adding staff.',
        showPassword: false
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setShowAddModal(false);
    setNewStaff({ firstName: '', lastName: '', email: '', phoneNumber: '', role: 'faculty' });
    setPhoneValue('');
    setSubmitStatus({ success: null, message: '', showPassword: false });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[99999] flex items-end sm:items-center justify-center p-0 sm:p-4 transition-opacity duration-200">
      <div 
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-xs transition-opacity" 
        onClick={handleClose}
      />

      <div className="relative w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-xl shadow-2xl transform transition-transform duration-300 ease-out flex flex-col max-h-[92vh] sm:max-h-none animate-slideUp sm:animate-scaleIn border border-slate-100">
        <div className="w-full flex justify-center py-2 sm:hidden shrink-0">
          <div className="w-10 h-1 bg-slate-200 rounded-full" />
        </div>

        <div className="px-4 pt-2 pb-3 sm:py-4 flex items-center justify-between border-b border-slate-100 shrink-0">
          <h2 className="text-base font-bold text-slate-900">Add New Staff Member</h2>
          <button 
            type="button" 
            onClick={handleClose} 
            className="p-1 text-slate-400 hover:text-slate-600 rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleAddSubmit} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 pb-8 sm:pb-4">
          {submitStatus.message && (
            <div className={`p-3 rounded-lg text-xs font-semibold ${
              submitStatus.success === true ? 'bg-slate-900 text-white' :
              submitStatus.success === false ? 'bg-red-50 text-red-700 border border-red-100' :
              'bg-slate-100 text-slate-700'
            }`}>
              <div className="flex items-center gap-2">
                {submitStatus.processing ? (
                  <Loader2 size={13} className="animate-spin text-slate-900" />
                ) : submitStatus.success === true ? (
                  <CheckCircle2 size={13} className="text-emerald-400 shrink-0" />
                ) : submitStatus.success === false ? (
                  <AlertCircle size={13} className="text-red-500 shrink-0" />
                ) : null}
                <span>{submitStatus.message}</span>
              </div>
              
              {submitStatus.showPassword && submitStatus.defaultPassword && (
                <div className="mt-2.5 p-2.5 bg-white border border-slate-200 rounded-lg text-slate-900">
                  <p className="text-[11px] text-slate-400 mb-1">Temporary Password</p>
                  <div className="flex items-center justify-between bg-slate-50 p-2 rounded-md gap-2">
                    <code className="text-xs font-mono font-bold select-all truncate">
                      {submitStatus.defaultPassword}
                    </code>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(submitStatus.defaultPassword);
                        alert('Password copied!');
                      }}
                      className="inline-flex items-center gap-1 text-[10px] font-bold bg-slate-900 text-white px-2 py-1 rounded-md shrink-0"
                    >
                      <Clipboard size={10} />
                      Copy
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newStaff.firstName || ''}
                onChange={(e) => handleFieldChange('firstName', e.target.value)}
                onBlur={() => handleBlur('firstName')}
                required
                className={getFieldClassName('firstName')}
                placeholder="First name"
                disabled={isProcessing || submitStatus.processing || isSubmitting}
              />
              {touched.firstName && formErrors.firstName && (
                <p className="mt-1 text-[11px] text-red-500 font-medium">{formErrors.firstName}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Last Name</label>
              <input
                type="text"
                value={newStaff.lastName || ''}
                onChange={(e) => setNewStaff({ ...newStaff, lastName: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-slate-900 focus:border-slate-900 transition-all h-10"
                placeholder="Last name (Optional)"
                disabled={isProcessing || submitStatus.processing || isSubmitting}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">
              Email Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="email"
                value={newStaff.email}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                onBlur={() => handleBlur('email')}
                onFocus={() => setShowSuggestions(true)}
                required
                className={getFieldClassName('email')}
                placeholder="email@institution.edu"
                disabled={isProcessing || submitStatus.processing || isSubmitting}
              />
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
                  {suggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="w-full text-left px-3 py-2 text-xs text-slate-600 hover:bg-slate-50"
                      onClick={() => {
                        const [localPart] = newStaff.email.split('@');
                        handleFieldChange('email', `${localPart}@gmail.com`);
                        setShowSuggestions(false);
                      }}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {touched.email && formErrors.email && (
              <p className="mt-1 text-[11px] text-red-500 font-medium">{formErrors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <div className="phone-input-container-custom">
              <PhoneInput
                country={'in'}
                value={phoneValue}
                onChange={handlePhoneChange}
                inputClass="!w-full !pl-11 !pr-3 !py-2 !h-10 !bg-slate-50 focus:!bg-white !border !border-slate-200 focus:!ring-1 focus:!ring-slate-900 focus:!border-slate-900 !rounded-lg !text-sm"
                buttonClass="!border !border-slate-200 !rounded-l-lg !bg-slate-100 !h-10"
                onBlur={() => handleBlur('phoneNumber')}
                disabled={isProcessing || submitStatus.processing || isSubmitting}
              />
            </div>
            {touched.phoneNumber && formErrors.phoneNumber && (
              <p className="mt-1 text-[11px] text-red-500 font-medium">{formErrors.phoneNumber}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              value={newStaff.role}
              onChange={(e) => handleFieldChange('role', e.target.value)}
              onBlur={() => handleBlur('role')}
              required
              className={getFieldClassName('role')}
              disabled={isProcessing || submitStatus.processing || isSubmitting}
            >
              <option value="">Select role</option>
              {roleOptions.map((role, index) => (
                <option key={index} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
            {touched.role && formErrors.role && (
              <p className="mt-1 text-[11px] text-red-500 font-medium">{formErrors.role}</p>
            )}
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end items-stretch sm:items-center gap-2 pt-3 border-t border-slate-100 mt-5 shrink-0">
            <button
              type="button"
              onClick={handleClose}
              disabled={isProcessing || submitStatus.processing || isSubmitting}
              className="h-10 sm:h-9 px-4 text-xs font-bold text-slate-600 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing || submitStatus.processing || isSubmitting || Object.keys(formErrors).length > 0}
              className={`h-10 sm:h-9 px-4 text-xs font-bold text-white rounded-lg transition-colors flex items-center justify-center ${
                isProcessing || submitStatus.processing || isSubmitting || Object.keys(formErrors).length > 0
                  ? 'bg-slate-300 cursor-not-allowed'
                  : 'bg-slate-900 hover:bg-slate-800'
              }`}
            >
              {submitStatus.processing || isSubmitting ? (
                <span className="flex items-center gap-1.5">
                  <Loader2 size={12} className="animate-spin" />
                  <span>Enrolling...</span>
                </span>
              ) : (
                'Add Staff'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddStaffModal;